import React, { useState } from 'react';

const MultiSelectWithAddonsStep = ({ question, value = {}, onAnswer, onNext }) => {
  // value structure: { selectedEffects: ['fatigue', 'nausea'], additives: { fatigue: 'b12', nausea: 'b6' } }
  const [selectedEffects, setSelectedEffects] = useState(value?.selectedEffects || []);
  const [additives, setAdditives] = useState(value?.additives || {});

  const isNoneOption = (optionValue) => {
    return optionValue === 'none';
  };

  const toggleEffect = (optionValue) => {
    const isNone = isNoneOption(optionValue);
    
    if (selectedEffects.includes(optionValue)) {
      // Deselecting
      const newEffects = selectedEffects.filter(v => v !== optionValue);
      setSelectedEffects(newEffects);
      // Remove additive for this effect
      const newAdditives = { ...additives };
      delete newAdditives[optionValue];
      setAdditives(newAdditives);
      onAnswer({ selectedEffects: newEffects, additives: newAdditives });
    } else {
      // Selecting
      if (isNone) {
        // If selecting "none", clear all
        setSelectedEffects([optionValue]);
        setAdditives({});
        onAnswer({ selectedEffects: [optionValue], additives: {} });
      } else {
        // Add this effect, remove "none" if present
        const newEffects = [...selectedEffects.filter(v => !isNoneOption(v)), optionValue];
        setSelectedEffects(newEffects);
        onAnswer({ selectedEffects: newEffects, additives });
      }
    }
  };

  const selectAdditive = (effectValue, additiveValue) => {
    const newAdditives = { ...additives, [effectValue]: additiveValue };
    setAdditives(newAdditives);
    onAnswer({ selectedEffects, additives: newAdditives });
  };

  const hasSelection = selectedEffects.length > 0;

  return (
    <div data-testid={`step-${question.id}`}>
      {/* Question */}
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">
        {question.question}
      </h2>

      {/* Subtitle */}
      <p className="text-center text-gray-500 mb-8">
        {question.subtitle || 'Select all that apply'}
      </p>

      {/* Options */}
      <div className="space-y-4 mb-8">
        {question.options.map((option, index) => {
          const optionValue = option.value;
          const isSelected = selectedEffects.includes(optionValue);
          const isNone = isNoneOption(optionValue);
          const hasAdditives = option.hasOptionalAdditive && option.additives;
          const showAdditives = isSelected && hasAdditives && !isNone;
          
          return (
            <div key={index} className="space-y-2">
              {/* Main checkbox option */}
              <button
                onClick={() => toggleEffect(optionValue)}
                className={`checkbox-card w-full p-5 rounded-xl text-left transition-all ${
                  isSelected ? 'selected' : ''
                }`}
                data-testid={`option-${index}`}
              >
                <div className="flex items-center gap-4">
                  {/* Checkbox */}
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                    isSelected ? 'bg-[#2d8a5f] border-[#2d8a5f]' : 'border-gray-300'
                  }`}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <span className={`text-base font-medium ${isNone ? 'text-gray-600' : 'text-gray-800'}`}>
                    {option.label}
                  </span>
                </div>
              </button>

              {/* Optional additives dropdown - shown when effect is selected */}
              {showAdditives && (
                <div className="ml-9 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {option.additiveLabel || 'Optional: Select an additive'}
                  </label>
                  <select
                    value={additives[optionValue] || ''}
                    onChange={(e) => selectAdditive(optionValue, e.target.value)}
                    className="form-input w-full"
                    data-testid={`additive-${optionValue}`}
                  >
                    <option value="">-- Select an additive (optional) --</option>
                    {option.additives.map((additive, addIndex) => (
                      <option key={addIndex} value={additive.value}>
                        {additive.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Continue Button */}
      <button
        onClick={onNext}
        disabled={!hasSelection}
        className="btn-primary"
        data-testid="continue-button"
      >
        Continue
      </button>
    </div>
  );
};

export default MultiSelectWithAddonsStep;
